import DesignTokens from '@tableau/design-tokens';
import { TabStyles, Tooltip, TooltipPosition } from '@tableau/widgets';
import { BodyCell } from '@tableau/widgets-datagrid';
import React from 'react';
import _ from 'underscore';

import { CellWithCountAndToolTip } from '~commonInfra/components/dataGrid/CellWithCount';
import { L10n } from '~commonInfra/L10n/L10n';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { SizeFilter } from '~commonInfra/utils/SizeFilter';
import { Messages } from '~src/codegen/Localize';
import { applyColor } from '~src/style/ColorService';

import { cssClass, textEllipsis } from '../../../../style';
import { ExplorerStyles } from '../../../components/Explorer/ExplorerStyles';
import { SpacesSiteUrls } from '../../spaceComponents/SpacesSiteUrls';
import { RightAlignedCell, StyledCellFormatter } from '../StyledCellWrapper';

export function PlainTextCell(propertyName: string, testId?: string, styleOverride?: React.CSSProperties): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function PlainTextCell(props: BodyCell.Props) {
      const text = props.row.data[propertyName];
      return <span title={text} data-tb-test-id={testId}>{text}</span>;
    },
    styleOverride
  );
}

export function PlainTextCellWithNestedValue(propertyName: string, nestedPropertyName: string, testId?: string, styleOverride?: React.CSSProperties): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function PlainTextCell(props: BodyCell.Props) {
      const text = props.row.data[propertyName][nestedPropertyName];
      return <span title={text} data-tb-test-id={testId}>{text}</span>;
    },
    styleOverride
  );
}

export function PlainTextCellWithNestedValueAndTransform(propertyName: string, nestedPropertyName: string,
  transform: (value: any) => string, testId?: string, styleOverride?: React.CSSProperties): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function PlainTextCellWithNestedValueAndTransform(props: BodyCell.Props) {
      const initialText = props.row.data[propertyName][nestedPropertyName];
      const text = transform(initialText);
      return <span title={text} data-tb-test-id={testId}>{text}</span>;
    },
    styleOverride
  );
}

export function PlainTextCellWithTransform(propertyName: string, transform: (value: any) => string | null,
  testId?: string, styleOverride?: React.CSSProperties): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function PlainTextCellWithTransform(props: BodyCell.Props) {
      const text = transform(props.row.data[propertyName]);
      return <span title={text} data-tb-test-id={testId}>{text}</span>;
    },
    styleOverride
  );
}

export function PlainTextCellWithTransformAndTooltip(propertyName: string,
  transform: (value: any) => string,
  getTooltip: (value: any) => string,
  testId?: string,
  styleOverride?: React.CSSProperties, tooltip?: string): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function PlainTextCellWithTransform(props: BodyCell.Props) {
      const propertyValue = props.row.data[propertyName];
      const text = transform(propertyValue);
      const tooltip = getTooltip(propertyValue);
      return <span title={tooltip} data-tb-test-id={testId}>{text}</span>;
    },
    styleOverride
  );
}

const dataGridCellText = cssClass('plainTextCellWithCount', 'text', textEllipsis, {
  display: 'block'
});

export function CreateCellText(text: string): JSX.Element {
  return (
    <span data-tb-test-id="data-grid-cell-text"
      className={dataGridCellText}
      title={text}>
      {text}
    </span>
  );
}

export function PlainTextCellWithCount(text: string,
  getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>,
  count?: string,
  testId?: string): React.StatelessComponent<BodyCell.Props> {

  return function PlainTextCellWithCount(props) {

    const getContent = getPopoverContent ? () => getPopoverContent(props) : undefined;
    return (
      <CellWithCountAndToolTip count={count} testId={testId} getPopoverContent={getContent}>
        {CreateCellText(text)}
      </CellWithCountAndToolTip>
    );
  };
}

export interface ITextWithIconSpecifiedByUrl {
  text: string;
  title?: string;
  textStyle?: React.CSSProperties;
  iconTitle?: string;
  iconUrl: string;
  hideTooltips?: boolean;
}


export interface ITextWithIconSpecifiedByClassWithBackgroundImage {
  text: string;
  title?: string;
  textStyle?: React.CSSProperties;
  iconTitle?: string;
  iconClass: string;
  hideTooltips?: boolean;
}

export function PlainTextCellWithIcon(metadataFn: (rowData: any) => ITextWithIconSpecifiedByClassWithBackgroundImage,
  testId?: string): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function PlainTextCellWithIcon(props) {
      const data = metadataFn(props.row.data);

      return (
        <span data-tb-test-id={testId || 'plain-text-cell-icon'}>
          <span data-tb-test-id="data-grid-cell-icon"
            className={data.iconClass}
            title={data.hideTooltips ? null : data.iconTitle} />
          <span data-tb-test-id="data-grid-cell-text"
            title={data.hideTooltips ? null : (data.title || data.text)}>
            {data.text}
          </span>
        </span>
      );
    }
  );
}

export interface PlainTextCellWithIconAndCountProps extends BodyCell.Props {
  metadataFn: (rowData: any) => ITextWithIconSpecifiedByUrl;
  getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>;
  count?: string;
  testId?: string;
}

export const PlainTextCellWithIconAndCount = (props: PlainTextCellWithIconAndCountProps, sapceSiteUrl: SpacesSiteUrls): JSX.Element => {
  const data = props.metadataFn(props.row.data);
  const textStyles = _.extend({ display: 'block' }, CommonStyles.Layout.TextEllipsis, data.textStyle);

  /* eslint-disable jsx-a11y/alt-text */
  const Icon = () => <img data-tb-test-id="data-grid-cell-icon"
    style={ExplorerStyles.connectsToListViewIconStyle}
    src={data.iconUrl}
    title={data.iconTitle} />;

  const Text = () => (
    <span data-tb-test-id="data-grid-cell-text"
      style={textStyles}
      title={data.hideTooltips ? null : (data.title || data.text)}>
      {data.text}
    </span>
  );

  const getContent = props.getPopoverContent ? () => props.getPopoverContent(props) : undefined;
  return (
    <CellWithCountAndToolTip count={props.count} testId={props.testId} getPopoverContent={getContent} wrapWholeCell={true}>
      <Icon />
      <Text />
    </CellWithCountAndToolTip>
  );
};

export function LocalizedCountCell(property: string | ((item: any) => number), testId?: string): React.StatelessComponent<BodyCell.Props> {
  return function LocalizedCountCell(props: BodyCell.Props) {
    let count;
    if (property instanceof Function) {
      count = property(props.row.data);
    } else {
      count = props.row.data[property];
    }
    const localizedCount = L10n.localizedCount(count);
    return <span data-tb-test-id={testId}>{_.isNumber(count) ? localizedCount : ''}</span>;
  };
}

export function LocalizedCountCellWithTooltip(
  property: string | ((item: any) => number),
  tooltipContentFn: (rowData: any) => React.ReactNode,
  testId?: string
): React.StatelessComponent<BodyCell.Props> {
  return function LocalizedCountCellWithTooltip(props: BodyCell.Props) {
    let count;
    if (property instanceof Function) {
      count = property(props.row.data);
    } else {
      count = props.row.data[property];
    }
    const localizedCount = L10n.localizedCount(count);
    const tooltipContent = tooltipContentFn(props.row.data);
    const content = <span data-tb-test-id={testId}>{_.isNumber(count) ? localizedCount : ''}</span>;

    if (tooltipContent) {
      return (
        <Tooltip tooltipContent={tooltipContent as React.ReactChild} position={TooltipPosition.Right}>
          {content}
        </Tooltip>
      );
    }
    return content;
  };
}

export function LocalizedFloatCell(property: string | ((item: any) => number), testId?: string) {
  return function LocalizedFloatCell(props: BodyCell.Props) {
    let float;
    if (property instanceof Function) {
      float = property(props.row.data);
    } else {
      float = props.row.data[property];
    }
    const localizedFloat = L10n.localizedFloat(float);
    return <span data-tb-test-id={testId}>{localizedFloat ? localizedFloat : ''}</span>;
  };
}

export function LocalizedFloatCellWithDefaultValue(property: string | ((item: any) => number), defaultValue: string, testId?: string) {
  return function LocalizedFloatCell(props: BodyCell.Props) {
    let float;
    if (property instanceof Function) {
      float = property(props.row.data);
    } else {
      float = props.row.data[property];
    }
    const localizedFloat = L10n.localizedFloat(float);
    defaultValue = (defaultValue === undefined) ? '' : defaultValue;
    return <span data-tb-test-id={testId}>{localizedFloat ? localizedFloat : defaultValue}</span>;
  };
}

export function SizeCell(propertyName: string, testId?: string): React.StatelessComponent<BodyCell.Props> {
  return function SizeCell(props: BodyCell.Props) {
    const sizeInBytes = props.row.data[propertyName];
    return <span data-tb-test-id={testId}>{SizeFilter(sizeInBytes)}</span>;
  };
}

/**
 * Renders a cell containing a number with a horizontal bar, the width of which is proportional
 * to the number's magnitude. This effectively turns the entire column into a bar graph. The
 * cell's value is returned by dataValueFn, and the bar sizes are normalized to the return value
 * of maxValueFn. For example, if dataValueFn returns 5 and maxValueFn returns 10, the cell will
 * display 5 and the bar will occupy 50% of the column's width. maxValueFn should return the same
 * value for every row in the grid. If the cell value is greater than 0, then the minimum size of the bar
 * will be 2 pixel.
 *
 * @param dataValueFn A function that returns the cell's data value from the row's data object
 * @param maxValueFn A function that returns the maximum value of all the items from the row's data object
 * @param barElementTestId An optional test ID to override the default.
 * @returns {Factory<BodyCell.Props>}
 * @constructor
 */
export function LocalizedCountCellWithBar(
  dataValueFn: (data: any) => number,
  maxValueFn: (data: any) => number,
  barElementTestId?: string
): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function LocalizedCountCellWithBar(props: BodyCell.Props) {
      const count = dataValueFn(props.row.data);
      const max = maxValueFn(props.row.data);

      let displayBarLength: number;
      if (!_.isNumber(max) || max <= 0 || !_.isNumber(count) || count <= 0) {
        displayBarLength = 0;
      } else {
        displayBarLength = (count / max);
      }
      const minBarLength = count > 0 && displayBarLength > 0 ? '2px' : '0px';
      const barVerticalMargin = TabStyles.Spacing.DefaultSpace;
      const barStyle: React.CSSProperties = {
        marginTop: barVerticalMargin,
        height: ExplorerStyles.LIST_VIEW_ROW_OUTER_HEIGHT - (2 * barVerticalMargin),
        width: (displayBarLength * 100) + '%',
        minWidth: minBarLength,
        backgroundColor: props.isSelected ? applyColor(DesignTokens.color.component.background.neutral.default.active, CommonStyles.Colors.Grey245) : applyColor(DesignTokens.color.component.background.neutral.default.default, CommonStyles.Colors.Grey218),
      };

      const textContainerStyle: React.CSSProperties = {
        lineHeight: ExplorerStyles.LIST_VIEW_ROW_OUTER_HEIGHT - (2 * barVerticalMargin) + 'px',
        paddingLeft: CommonStyles.Sizes.DefaultSpace,
        verticalAlign: 'top'
      };

      return (
        <div data-tb-test-id={barElementTestId || 'metrics-cell-bars'} style={barStyle}>
          <span style={textContainerStyle}>{_.isNumber(count) ? L10n.localizedCount(count) : ''}</span>
        </div>
      );
    }
  );
}

export function FixedValueCell(value: string): React.StatelessComponent<any> {
  return RightAlignedCell(
    function FixedValueCell() {
      return <span title={value}>{value}</span>;
    }
  );
}

// RecycleBin-specific cell formatters
// Using #BFBFBF color to be consistent with the established pattern for hidden users in the codebase
export function PlainTextCellWithTransformAndGrayHiddenUser(propertyName: string, transform: (value: any) => string | null,
  testId?: string, styleOverride?: React.CSSProperties): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function PlainTextCellWithTransformAndGrayHiddenUser(props: BodyCell.Props) {
      const text = transform(props.row.data[propertyName]);
      const isHiddenUser = text === Messages.hiddenUser();
      const textStyle = isHiddenUser ? { color: applyColor(DesignTokens.color.font.default.subtle, '#bfbfbf') } : {};
      return <span title={text} data-tb-test-id={testId} style={textStyle}>{text}</span>;
    },
    styleOverride
  );
}

export function PlainTextCellWithNullToHiddenTransform(propertyName: string, testId?: string, styleOverride?: React.CSSProperties): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function PlainTextCellWithNullToHiddenTransform(props: BodyCell.Props) {
      const rawValue = props.row.data[propertyName];
      const isHidden = !rawValue || rawValue === 'Hidden';
      const text = isHidden ? Messages.hidden() : rawValue;
      const textStyle = isHidden ? { color: applyColor(DesignTokens.color.font.default.subtle, '#bfbfbf') } : {};
      return <span title={text} data-tb-test-id={testId} style={textStyle}>{text}</span>;
    },
    styleOverride
  );
}
